import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Grid, Stack, Typography, useMediaQuery, useTheme, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { Navbar } from '../features/navigation/components/Navbar'
import { Footer } from '../features/footer/Footer'
import { ReviewGalleryItem } from '../components/ReviewGalleryItem'
import { axiosi } from '../config/axios'

export const ReviewsGalleryPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('recent')
  const [filterRating, setFilterRating] = useState('all')

  useEffect(() => {
    fetchReviews()
  }, [])

  const fetchReviews = async () => {
    try {
      setLoading(true)
      const response = await axiosi.get('/reviews?page=1&limit=100')
      // Filter only reviews with photos or videos
      const mediaReviews = response.data.filter(review =>
        (review.photoUrls && review.photoUrls.length > 0) || review.videoUrl
      )
      setReviews(mediaReviews)
    } catch (error) {
      console.error('Error fetching reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  const getFilteredAndSortedReviews = () => {
    let filtered = [...reviews]

    // Filter by rating
    if (filterRating !== 'all') {
      filtered = filtered.filter(r => r.rating === parseInt(filterRating))
    }

    // Sort
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        break
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        break
      case 'rating-high':
        filtered.sort((a, b) => b.rating - a.rating)
        break
      case 'rating-low':
        filtered.sort((a, b) => a.rating - b.rating)
        break
      default:
        break
    }

    return filtered
  }

  const filteredReviews = getFilteredAndSortedReviews()

  return (
    <>
      <Navbar />
      
      <Box sx={{ minHeight: '80vh', py: 6, px: isMobile ? 2 : 4, backgroundColor: '#f9f9f9' }}>
        <Stack spacing={4}>
          {/* Header */}
          <Stack spacing={2} textAlign="center">
            <Typography 
              variant="h2" 
              sx={{
                fontWeight: 900,
                fontSize: isMobile ? '2.15rem' : '3.25rem',
                color: 'primary.main',
                lineHeight: 1.1
              }}
            >
              Customer Reviews Gallery
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              See what our customers are saying - Real photos and videos from real users
            </Typography>
          </Stack>

          {/* Filters */}
          {!loading && reviews.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'white',
                p: 2,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="recent">Most Recent</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                  <MenuItem value="rating-high">Highest Rated</MenuItem>
                  <MenuItem value="rating-low">Lowest Rated</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Rating</InputLabel>
                <Select
                  value={filterRating}
                  label="Rating"
                  onChange={(e) => setFilterRating(e.target.value)}
                >
                  <MenuItem value="all">All Ratings</MenuItem>
                  <MenuItem value="5">⭐⭐⭐⭐⭐ (5 Stars)</MenuItem>
                  <MenuItem value="4">⭐⭐⭐⭐ (4 Stars)</MenuItem>
                  <MenuItem value="3">⭐⭐⭐ (3 Stars)</MenuItem>
                  <MenuItem value="2">⭐⭐ (2 Stars)</MenuItem>
                  <MenuItem value="1">⭐ (1 Star)</MenuItem>
                </Select>
              </FormControl>

              <Chip 
                label={`${filteredReviews.length} review${filteredReviews.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          {/* Loading */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredReviews.length > 0 ? (
            <Grid container spacing={3}>
              {filteredReviews.map((review) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={review._id}>
                  <ReviewGalleryItem review={review} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                {reviews.length === 0 
                  ? 'No reviews with photos yet' 
                  : 'No reviews match your filters'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Be the first to share your experience with photos!
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Footer />
    </>
  )
}
